/** Matches @key references in scenario text (must start with a letter). */
export const TEXT_PLACEHOLDER_PATTERN = /@([a-zA-Z][a-zA-Z0-9_-]*)/g;

export const TEXT_PLACEHOLDER_KEY_RE = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

/** Matches an active @query being typed at end of string (for autocomplete). */
export const TEXT_PLACEHOLDER_TRIGGER_PATTERN = /@([a-zA-Z0-9_-]*)$/;

export function resolveTextPlaceholders(
  text: string,
  placeholders: Record<string, string> | undefined
): string {
  if (!placeholders || Object.keys(placeholders).length === 0) return text;
  return text.replace(TEXT_PLACEHOLDER_PATTERN, (match, key: string) =>
    key in placeholders ? placeholders[key] : match
  );
}

export interface PlaceholderTriggerMatch {
  query: string;
  startIndex: number;
}

export function findPlaceholderTrigger(
  text: string,
  cursorIndex: number
): PlaceholderTriggerMatch | null {
  const beforeCursor = text.slice(0, cursorIndex);
  const match = beforeCursor.match(TEXT_PLACEHOLDER_TRIGGER_PATTERN);
  if (!match || match.index == null) return null;
  return {
    query: match[1] ?? '',
    startIndex: match.index,
  };
}

export function getCaretCoordinates(
  textarea: HTMLTextAreaElement,
  position: number
): { top: number; left: number } {
  const div = document.createElement('div');
  const style = window.getComputedStyle(textarea);

  const properties = [
    'boxSizing',
    'width',
    'height',
    'overflowX',
    'overflowY',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'fontSizeAdjust',
    'lineHeight',
    'fontFamily',
    'textAlign',
    'textTransform',
    'textIndent',
    'textDecoration',
    'letterSpacing',
    'wordSpacing',
    'tabSize',
    'MozTabSize',
  ] as const;

  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  div.style.whiteSpace = 'pre-wrap';
  div.style.wordWrap = 'break-word';

  for (const prop of properties) {
    div.style[prop] = style[prop];
  }

  div.textContent = textarea.value.substring(0, position);
  const span = document.createElement('span');
  span.textContent = textarea.value.substring(position) || '.';
  div.appendChild(span);

  document.body.appendChild(div);
  const textareaRect = textarea.getBoundingClientRect();
  const spanRect = span.getBoundingClientRect();
  const divRect = div.getBoundingClientRect();
  document.body.removeChild(div);

  const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
  const top = textareaRect.top + (spanRect.top - divRect.top) - textarea.scrollTop + lineHeight;
  const left = textareaRect.left + (spanRect.left - divRect.left) - textarea.scrollLeft;

  return { top, left };
}
